import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { map, switchMap, switchMapTo, tap } from 'rxjs/operators'
import {
  SubscriptionAddonFeatureAccessLimited,
  SubscriptionAddonRentalCountLimited,
  SubscriptionExpired,
  SubscriptionFeatureAccessLimited,
  SubscriptionInvalidLimited,
  SubscriptionRentalCountLimited,
  SubscriptionTrialLimited,
  SubscriptionUserCountLimited,
} from './plan.actions'
import { AlertDialogService, ConfirmDialogService } from '@tokeet/tv3-platform'
import { Router } from '@angular/router'
import { capitalize } from 'lodash'
import { tokeetAddonProductNames } from './plan.model'
import { CloseAllDialogs } from '@tokeet-frontend/utility'

@Injectable()
export class SubscriptionEffects {
  @Effect()
  expired$ = this.actions$.pipe(
    ofType(SubscriptionExpired),
    switchMap(() =>
      this.alertDialog.openResult({
        title: 'Subscription Expired',
        body: `Your subscription has expired. Please select a new subscription or enter a new credit card to continue your
              Tokeet service.`,
      })
    ),
    tap(() => this.gotoPlanPage()),
    map(() => CloseAllDialogs())
  )

  @Effect()
  trialLimited$ = this.actions$.pipe(
    ofType(SubscriptionTrialLimited),
    switchMap(() =>
      this.alertDialog.openResult({
        title: 'Subscribe to a paid plan',
        body: `You must subscribe to a paid plan before you can access these features.`,
      })
    ),
    tap(() => this.gotoPlanPage()),
    map(() => CloseAllDialogs())
  )

  @Effect()
  invalid$ = this.actions$.pipe(
    ofType(SubscriptionInvalidLimited),
    switchMap(() =>
      this.alertDialog.openResult({
        title: 'Subscribe to a plan',
        body: `You must subscribe to a paid plan before you can access these features.`,
      })
    ),
    tap(() => this.gotoPlanPage()),
    map(() => CloseAllDialogs())
  )

  @Effect()
  accessLimited$ = this.actions$.pipe(
    ofType(SubscriptionFeatureAccessLimited),
    switchMap(({ currentPlanType, requiredPlanType }) =>
      this.alertDialog.openResult({
        title: 'Paid subscription required',
        body: `You must subscribe a paid plan before you can access these features.`,
      })
    ),
    tap(() => this.gotoPlanPage()),
    map(() => CloseAllDialogs())
  )

  @Effect()
  addonAccessLimited$ = this.actions$.pipe(
    ofType(SubscriptionAddonFeatureAccessLimited),
    switchMap(({ addonId }) =>
      this.alertDialog.openResult({
        title: 'You Must Subscribe',
        body: `You must subscribe to ${tokeetAddonProductNames[addonId]} before you may access this feature.`,
      })
    ),
    tap(() => this.gotoPlanPage()),
    map(() => CloseAllDialogs())
  )

  @Effect()
  userCountLimited$ = this.actions$.pipe(
    ofType(SubscriptionUserCountLimited),
    switchMap(({ currentPlanType, current, limit }) =>
      this.confirmDialog.confirm({
        title: 'You need to upgrade',
        body: `You must subscribe to a paid plan before you can access these features.`,
      })
    ),
    tap(() => this.gotoPlanPage()),
    map(() => CloseAllDialogs())
  )

  @Effect()
  rentalCountLimited$ = this.actions$.pipe(
    ofType(SubscriptionRentalCountLimited),
    switchMap(({ currentPlanType, current, limit }) =>
      this.confirmDialog.confirm({
        title: 'You need to upgrade',
        body: `You must subscribe to a paid plan before you can access these features.`,
      })
    ),
    tap(() => this.gotoPlanPage()),
    map(() => CloseAllDialogs())
  )

  @Effect({ dispatch: false })
  addonRentalCountLimited$ = this.actions$.pipe(
    ofType(SubscriptionAddonRentalCountLimited),
    switchMap(({ addonPlans, current }) =>
      this.confirmDialog.confirm({
        title: 'You need to upgrade',
        body: `You have exceeded the allowable number of rentals on one or more of your add-on subscriptions. Please upgrade to ensure that the add-on subscriptions are appropriate for the number of rentals on your Tokeet account. To do this, toggle the subscription status to Inactive and back to Active. The appropriate plan for your number of rentals is preselected. <br/>
          ${addonPlans.map((p) => `<strong>${p.planName}</strong>: ${p.rentals} rental(s) <br\>`).join('')}`,
      })
    ),
    tap(() => this.gotoPlanPage())
  )

  gotoPlanPage() {
    this.router.navigate(['/billing/info'])
  }

  constructor(
    private actions$: Actions,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private alertDialog: AlertDialogService
  ) {}
}
